// apps/web/src/pages/PreApproval/PreApprovalBanner.tsx

import { useState } from 'react';

import { AlertTriangle, ArrowRight, GraduationCap, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@alamo/auth';
import { Button } from '@alamo/ui';

import { useRegistrationAttention } from '../../hooks/useRegistrationAttention';

const SESSION_KEY = 'pre_approval_banner_dismissed';

/** Correções ficam visíveis até a resolução, com prioridade sobre a orientação de treinamento. */
export function PreApprovalBanner() {
  const { t } = useTranslation('pre_approval');
  const { onboardingCompletion } = useAuth();
  const { needsCorrection } = useRegistrationAttention();
  const onboardingBlocked = onboardingCompletion?.is_blocked === true;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed && !onboardingBlocked && !needsCorrection) return null;
  const showTraining = onboardingBlocked && !needsCorrection;
  const Icon = needsCorrection ? AlertTriangle : Info;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-start gap-3 border-b border-sel-tint-laranja bg-laranja-encanto-04 px-4 py-3 sm:flex-nowrap sm:items-center"
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-laranja-encanto" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-sm font-body leading-6 text-secondary-950">
        {needsCorrection && <strong className="block">{t('banner.correction_title')}</strong>}
        {t(
          needsCorrection
            ? 'banner.correction_text'
            : showTraining
              ? 'banner.onboarding_text'
              : 'banner.text',
        )}
      </p>
      <Link
        to={showTraining ? '/training' : '/my-registration'}
        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-alamo-surface px-3 text-sm font-body font-semibold text-azul-protecao transition-colors hover:bg-sel-tint-laranja focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-protecao sm:w-auto sm:shrink-0"
      >
        {showTraining && <GraduationCap className="h-4 w-4" aria-hidden="true" />}
        {t(
          needsCorrection
            ? 'banner.correction_action'
            : showTraining
              ? 'banner.start_training'
              : 'banner.view_details',
        )}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
      {!onboardingBlocked && !needsCorrection && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setDismissed(true);
            try {
              sessionStorage.setItem(SESSION_KEY, 'true');
            } catch {
              // sessionStorage may be unavailable
            }
          }}
          className="shrink-0 text-secondary-950 hover:bg-sel-tint-laranja"
          aria-label={t('banner.dismiss')}
        >
          {t('banner.dismiss')}
        </Button>
      )}
    </div>
  );
}
